import { baseApi as api } from "./baseApi";
export const addTagTypes = [
  "internal",
  "patients",
  "customers",
  "emr-auth",
] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      healthcheck: build.query<HealthcheckApiResponse, HealthcheckApiArg>({
        query: () => ({ url: `/healthcheck` }),
        providesTags: [],
      }),
      internalAuthCheck: build.query<
        InternalAuthCheckApiResponse,
        InternalAuthCheckApiArg
      >({
        query: () => ({ url: `/internal/auth-check` }),
        providesTags: ["internal"],
      }),
      searchPatients: build.mutation<
        SearchPatientsApiResponse,
        SearchPatientsApiArg
      >({
        query: (queryArg) => ({
          url: `/patients/search`,
          method: "POST",
          body: queryArg.patientsSearchRequest,
        }),
        invalidatesTags: ["patients"],
      }),
      getEncounters: build.query<GetEncountersApiResponse, GetEncountersApiArg>(
        {
          query: (queryArg) => ({
            url: `/encounters`,
            params: {
              customer_regard_id: queryArg.customerRegardId,
              regard_patient_id: queryArg.regardPatientId,
            },
          }),
          providesTags: ["patients"],
        },
      ),
      getNotes: build.query<GetNotesApiResponse, GetNotesApiArg>({
        query: (queryArg) => ({
          url: `/notes`,
          params: {
            customer_regard_id: queryArg.customerRegardId,
            regard_patient_id: queryArg.regardPatientId,
          },
        }),
        providesTags: ["patients"],
      }),
      getNoteContent: build.query<
        GetNoteContentApiResponse,
        GetNoteContentApiArg
      >({
        query: (queryArg) => ({
          url: `/notes/${queryArg.noteId}/content`,
          params: {
            customer_regard_id: queryArg.customerRegardId,
          },
        }),
        providesTags: ["patients"],
      }),
      getCustomers: build.query<GetCustomersApiResponse, GetCustomersApiArg>({
        query: () => ({ url: `/customers` }),
        providesTags: ["customers"],
      }),
      redeemEmrSession: build.mutation<
        RedeemEmrSessionApiResponse,
        RedeemEmrSessionApiArg
      >({
        query: (queryArg) => ({
          url: `/auth/emr/session`,
          method: "POST",
          body: queryArg.sessionRequest,
        }),
        invalidatesTags: ["emr-auth"],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as api };
export type HealthcheckApiResponse =
  /** status 200 Successful Response */ HealthCheckResponse;
export type HealthcheckApiArg = void;
export type InternalAuthCheckApiResponse =
  /** status 200 Successful Response */ InternalAuthCheckResponse;
export type InternalAuthCheckApiArg = void;
export type SearchPatientsApiResponse =
  /** status 200 Successful Response */ PatientsResponse;
export type SearchPatientsApiArg = {
  patientsSearchRequest: PatientsSearchRequest;
};
export type GetEncountersApiResponse =
  /** status 200 Successful Response */ EncounterListResponse;
export type GetEncountersApiArg = {
  /** Regard customer identifier (e.g., 'adventhealth') */
  customerRegardId: string;
  /** Regard patient ID (UUID) */
  regardPatientId: string;
};
export type GetNotesApiResponse =
  /** status 200 Successful Response */ NotesResponse;
export type GetNotesApiArg = {
  /** Regard customer identifier (e.g., 'contoso') */
  customerRegardId: string;
  /** Regard patient ID (UUID) */
  regardPatientId: string;
};
export type GetNoteContentApiResponse =
  /** status 200 Successful Response */ NoteContentResponse;
export type GetNoteContentApiArg = {
  noteId: string;
  /** Regard customer identifier (e.g., 'contoso') */
  customerRegardId: string;
};
export type GetCustomersApiResponse =
  /** status 200 Successful Response */ CustomersResponse;
export type GetCustomersApiArg = void;
export type RedeemEmrSessionApiResponse =
  /** status 200 Successful Response */ SessionResponse;
export type RedeemEmrSessionApiArg = {
  sessionRequest: SessionRequest;
};
export type HealthCheckResponse = {
  activeThreadCount: number;
  timestamp: string;
  version: string;
};
export type InternalAuthCheckResponse = {
  ok: boolean;
  email: string;
  sub: string;
};
export type PatientMrnAlias = {
  name?: string | null;
  value?: string | null;
  system?: string | null;
  [key: string]: any;
};
export type PatientSummary = {
  fhirPatientId: string;
  regardPatientId: string;
  mrn?: string | null;
  mrns?: PatientMrnAlias[];
  firstName?: string | null;
  lastName?: string | null;
  updatedAtDatetime?: string | null;
  [key: string]: any;
};
export type PatientsResponse = {
  items: PatientSummary[];
  nextCursor?: string | null;
  [key: string]: any;
};
export type ValidationError = {
  loc: (string | number)[];
  msg: string;
  type: string;
  input?: any;
  ctx?: object;
};
export type HttpValidationError = {
  detail?: ValidationError[];
};
export type PatientsSearchRequest = {
  /** Regard customer identifier (e.g., 'contoso') */
  customer_regard_id: string;
  /** Optional list of MRNs to filter by. If null/empty, returns all. */
  mrns?: string[] | null;
  /** Optional list of Regard patient UUIDs to filter by. If null/empty, no filter applied. */
  regard_patient_ids?: string[] | null;
  /** Filter by analysis status. 'analyzed' returns only patients with at least one analysis run. 'not-analyzed' returns only patients without any analysis run. 'all' or null returns all patients. */
  analysis_status?: ("all" | "analyzed" | "not-analyzed") | null;
  /** Number of patients per page */
  limit?: number;
  /** Cursor for keyset pagination */
  cursor?: string | null;
};
export type EncounterListItem = {
  resourceId: string;
  resourceVersion: string;
  patientId: string;
  typeKey: string;
  fin: string | null;
  csn: string | null;
  statusKey: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  parentId: string | null;
  [key: string]: any;
};
export type EncounterListResponse = {
  encounters: EncounterListItem[];
  [key: string]: any;
};
export type NoteItem = {
  /** DocumentReference note identifier used as note identity in findings */
  sourceId: string;
  /** FHIR Binary identifier used when fetching note content */
  binarySourceId: string;
  /** ISO 8601 datetime when the note was created */
  effectiveAt: string;
  /** Identifier of the note author, if available */
  authorId: string | null;
  /** Human-readable name of the note author, if resolvable */
  authorName?: string | null;
  /** Comma-separated credentials of the note author (e.g., 'MD, FACP') */
  authorCredentials?: string | null;
  /** Human-readable note type labels (from note_kinds concepts) */
  noteTypes: string[];
  /** Status of the note (e.g., 'final', 'preliminary', 'amended') */
  status: string;
  /** Encounter ID associated with the note, if any */
  encounterId: string | null;
};
export type NotesResponse = {
  /** List of practitioner notes for the patient */
  notes: NoteItem[];
  /** The Regard patient ID (echoed from request) */
  regardPatientId: string;
  /** Total number of notes returned */
  count: number;
};
export type NoteContentResponse = {
  /** The FHIR Binary resource ID of the note */
  noteId: string;
  /** MIME type of the note content (e.g., text/html, application/pdf, text/xml) */
  contentType: string;
  /** Base64-encoded original content of the note */
  contentBase64: string;
};
export type CustomerSummary = {
  /** The Regard internal identifier for the customer (e.g., 'sentara') */
  customerRegardId: string;
  /** Human-readable customer name, falling back to the identifier when unset */
  displayName: string;
};
export type CustomersResponse = {
  /** Customers the authenticated caller is authorized to access */
  customers: CustomerSummary[];
};
export type SessionResponse = {
  access_token: string;
  token_type: string;
  expires_in: number;
};
export type SessionRequest = {
  handoff_code: string;
};
export const {
  useHealthcheckQuery,
  useLazyHealthcheckQuery,
  useInternalAuthCheckQuery,
  useLazyInternalAuthCheckQuery,
  useSearchPatientsMutation,
  useGetEncountersQuery,
  useLazyGetEncountersQuery,
  useGetNotesQuery,
  useLazyGetNotesQuery,
  useGetNoteContentQuery,
  useLazyGetNoteContentQuery,
  useGetCustomersQuery,
  useLazyGetCustomersQuery,
  useRedeemEmrSessionMutation,
} = injectedRtkApi;
